import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getPromptTitle, type SupportedLang } from "@/lib/prompt-utils";
import { PromptDTO } from "@shaarp/common";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";

export type PinnedPromptsContextListProps = {
  contextLabel: string;
  limitLabel: string;
  ids: string[];
  promptMap: Map<string, PromptDTO>;
  lang: SupportedLang;
  emptyStateLabel: string;
  moveUpAria: string;
  moveDownAria: string;
  removeAria: string;
  onRemove: (id: string) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  /** Pins placed by the team default — tagged with `teamDefaultLabel`. */
  teamDefaultIds?: ReadonlySet<string>;
  teamDefaultLabel?: string;
  /**
   * Pins the viewer may neither see nor touch (a member's personal tactics):
   * shown as `lockedLabel`, with no move or remove button, and never swapped
   * past by a neighbour's move.
   */
  lockedIds?: ReadonlySet<string>;
  lockedLabel?: string;
  lockedHint?: string;
};

const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * The ordered list half of a pinned-tactics editor — shared by the team's
 * default editor and the per-member editor, which differ only in what sits
 * below the list (an "apply to team" button on one, nothing on the other).
 *
 * Reordering is up/down buttons rather than drag-and-drop: both editors are
 * infrequent, admin-only screens, so `CardQuickActions`'s `dnd-kit` sorting —
 * built for a screen end users touch constantly — is more machinery than the
 * task needs here.
 */
export function PinnedPromptsContextList({
  contextLabel,
  limitLabel,
  ids,
  promptMap,
  lang,
  emptyStateLabel,
  moveUpAria,
  moveDownAria,
  removeAria,
  onRemove,
  onMove,
  teamDefaultIds = EMPTY_SET,
  teamDefaultLabel,
  lockedIds = EMPTY_SET,
  lockedLabel,
  lockedHint,
}: PinnedPromptsContextListProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-medium">{contextLabel}</span>
        <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {limitLabel}
        </span>
      </div>

      {ids.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyStateLabel}</p>
      ) : (
        <ul className="space-y-2">
          {ids.map((id, index) => {
            if (lockedIds.has(id)) {
              return (
                <li
                  key={id}
                  title={lockedHint}
                  className="flex items-center rounded-md border border-dashed px-3 py-2"
                >
                  <span className="flex-1 truncate text-sm italic text-muted-foreground">
                    {lockedLabel}
                  </span>
                </li>
              );
            }

            const prompt = promptMap.get(id);
            const canMoveUp = index > 0 && !lockedIds.has(ids[index - 1]);
            const canMoveDown =
              index < ids.length - 1 && !lockedIds.has(ids[index + 1]);
            return (
              <li
                key={id}
                className="flex items-center gap-1 rounded-md border px-3 py-2"
              >
                <span className="flex-1 truncate text-sm">
                  {prompt ? getPromptTitle(prompt, lang) : id}
                </span>
                {teamDefaultLabel && teamDefaultIds.has(id) && (
                  <Badge variant="secondary" className="shrink-0 font-normal">
                    {teamDefaultLabel}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={!canMoveUp}
                  aria-label={moveUpAria}
                  onClick={() => onMove(index, -1)}
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={!canMoveDown}
                  aria-label={moveDownAria}
                  onClick={() => onMove(index, 1)}
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  aria-label={removeAria}
                  onClick={() => onRemove(id)}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
